import { isAuthenticated } from "helpers";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, Link } from "react-router-dom/cjs/react-router-dom.min";
import routers from "routes/routes";
import { apiMethods, endPoints } from "service/endpoints";
import { request } from "service/request";
import Sidebar from "component/Common/Sidebar";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  LockClosedIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { Divider } from "@mui/material";
import NavBar from "component/Common/NavBar";
import ChoiceListContainer from "component/ChoiceList/ChoiceListContainer";
import LayoutContext from "context/layoutContext";
import { repoMapping } from "helpers/constant";
import { Helmet } from "react-helmet-async";
import Seo from "helpers/Seo";

export function NavigationLayout({ children }) {
  const dispatch = useDispatch();
  const data = useSelector((state) => state?.common?.metadata);
  const location = useLocation();
  const path = location.pathname;
  const history = useHistory();
  const selectionContainerRef = useRef();
  const listSearchRef = useRef();

  const [showSelectionContainer, setShowSelectionContainer] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [containerTrigger, setContainerTrigger] = useState(null);
  const [containerFeature, setContainerFeature] = useState(null);
  const [listSearch, setListSearch] = useState("");

  useEffect(() => {
    if (isAuthenticated()) {
      getMetaData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectionContainerRef.current && showSelectionContainer) {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("keydown", handleEscKey);
      return () => {
        document.removeEventListener("click", handleClickOutside);
        document.removeEventListener("keydown", handleEscKey);
      };
    }
  }, [showSelectionContainer]);

  useEffect(() => {
    if (showSelectionContainer) {
      const timer = setTimeout(() => {
        if (listSearchRef.current) listSearchRef.current.focus();
      }, 0); // Adjust delay if necessary
      return () => clearTimeout(timer);
    }
  }, [containerTrigger, showSelectionContainer]);

  useEffect(() => {
    if (containerTrigger) {
      setShowSelectionContainer(true);
    } else {
      handleSelectionContainerClose();
    }
  }, [containerTrigger]);

  const getMetaData = async () => {
    try {
      const results = await request({
        url: `${endPoints.user.GET_META}`,
        method: apiMethods.GET,
        showToast: false,
        isLoader: true,
        location: location,
      });
      if (results?.data) {
        dispatch({ type: "USER_DETAIL", payload: results.data.user });
        dispatch({ type: "GET_META_DATA", payload: results.data });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const filteredBySearchTermSelection = (featuredCounsellings) => {
    if (featuredCounsellings && Object.keys(featuredCounsellings).length > 0) {
      const returnCounsellings = Object.entries(featuredCounsellings)
        .map(([region, counselings]) => {
          const filteredCounselings = counselings.filter(
            (counseling) =>
              !listSearch || counseling.name.toLowerCase().includes(listSearch.toLowerCase())
          );
          if (filteredCounselings.length > 0) {
            return [region, filteredCounselings];
          }
          return null;
        })
        .filter(Boolean);
      return returnCounsellings;
    } else {
      return [];
    }
  };

  const handleContainerTrigger = (trigger) => {
    setContainerTrigger(trigger);
    let triggerFeature = data?.default_exam?.features?.find(
      (feature) => feature.feature === repoMapping[trigger]
    );
    setContainerFeature(triggerFeature);
  };

  const handleClickOutside = (event) => {
    if (selectionContainerRef.current && !selectionContainerRef.current.contains(event.target)) {
      handleSelectionContainerClose();
    }
  };

  const handleEscKey = (event) => {
    if (event.key === "Escape") {
      if (selectionContainerRef.current) {
        handleSelectionContainerClose();
      }
    }
  };

  const handleSelectionContainerClose = () => {
    setShowSelectionContainer(false);
    setContainerTrigger(null);
    setContainerFeature(null);
    setListSearch(null);
  };

  let selectionList = [...filteredBySearchTermSelection(containerFeature?.counsellings)] || [];

  return (
    <LayoutContext.Provider value={{ handleContainerTrigger }}>
      <Seo
        title="ZyNerd Portal"
        description="Access your personalized NEET counselling dashboard with college predictions, choice filling tools, and expert guidance."
        meta={[{ name: "robots", content: "noindex, nofollow" }]}
      />
      <Helmet>
        <meta name="viewport" content="width=1024" />
      </Helmet>
      <div className="flex relative h-screen w-full rounded bg-grey-50 !p-6 !gap-2 overflow-hidden">
        <Sidebar
          data={data?.default_exam?.features}
          selectedFeature={selectedFeature}
          setSelectedFeature={setSelectedFeature}
          containerTrigger={containerTrigger}
          handleContainerTrigger={handleContainerTrigger}
        />
        <div className="relative flex-1 flex flex-col gap-4 w-full h-full min-w-0">
          <NavBar
            data={data}
            getAllMeta={getMetaData}
            setContainerTrigger={handleContainerTrigger}
            selectedFeature={selectedFeature}
            setSelectedFeature={setSelectedFeature}
          />
          <div className={`flex overflow-auto h-full ${showSelectionContainer ? "blur-sm" : ""}`}>
            {children}
          </div>
          {showSelectionContainer && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex z-10 p-3 rounded-3xl">
              <div
                className="flex flex-col bg-white w-[50%] md:max-w-[456px] md:min-w-[360px] min-w-[300px] max-h-full px-6 py-8 rounded-2xl gap-3 overflow-y-auto"
                ref={selectionContainerRef}
              >
                {containerTrigger === "choiceTable" ? (
                  <ChoiceListContainer
                    handleSelectionContainerClose={handleSelectionContainerClose}
                  />
                ) : (
                  <div className="flex flex-col w-full gap-3 overflow-y-auto">
                    <div id="selectionContainerTitle" className="flex justify-between">
                      <p className="text-xl font-bold">{containerFeature?.feature}</p>
                      <span className="flex items-center justify-center">
                        <XMarkIcon
                          className="w-4 h-4 aspect-square stroke-bold cursor-pointer"
                          onClick={() => handleSelectionContainerClose()}
                        />
                      </span>
                    </div>
                    <div
                      className={`relative flex items-center justify-between w-full min-h-9 max-h-9 bg-grey-50 rounded-lg overflow-hidden`}
                    >
                      <input
                        ref={listSearchRef}
                        onChange={(e) => {
                          setListSearch(e.target.value);
                        }}
                        type="text"
                        placeholder="Search Counselling"
                        value={listSearch}
                        className="w-full text-grey-600 bg-grey-50 text-xs font-semibold py-2 pl-10 pr-3 focus-visible:outline-0 focus:!outline-offset-0"
                      />
                      <MagnifyingGlassIcon className="absolute left-3 w-4 h-4 object-cover" />
                    </div>
                    <div className="flex flex-col overflow-auto scrollbar-hover ">
                      {containerFeature?.counsellings &&
                      Object.keys(containerFeature?.counsellings)?.length > 0 ? (
                        selectionList.length > 0 ? (
                          selectionList?.map(([region, counselings], index) => {
                            return counselings?.map((counseling, counselingIndex) => {
                              let isLast =
                                Object.keys(containerFeature?.counsellings)?.length === index + 1 &&
                                counselingIndex === counselings?.length - 1;
                              return (
                                <div className={`flex flex-col w-full`} key={index}>
                                  <Link
                                    key={counseling.id}
                                    to={`/portal/${containerTrigger}?counselling_id=${counseling.id}`}
                                    onClick={() => handleSelectionContainerClose()}
                                    className={`flex justify-between items-center w-full !pl-3 !pr-2 !py-2  ${
                                      !isLast ? "mt-1 mb-1" : "mt-1"
                                    } rounded hover:bg-grey-100 hover:rounded-xl`}
                                  >
                                    <div
                                      className="flex w-full h-full py-1 items-center justify-between menu-items rounded"
                                      key={counseling.id}
                                    >
                                      <p className={` text-sm font-medium`}>{counseling.name}</p>
                                      {counseling?.access_state === "LIMITED" && (
                                        <div className="flex  items-center justify-center min-w-8 min-h-8 rounded-full bg-grey-100">
                                          <LockClosedIcon className="w-4 h-4 object-cover stroke-2" />
                                        </div>
                                      )}
                                    </div>
                                    <ChevronRightIcon className="w-4 h-4 min-w-4 max-w-4 stroke-2" />
                                  </Link>
                                  {!isLast && <Divider sx={{ borderWidth: 1, width: "100%" }} />}
                                </div>
                              );
                            });
                          })
                        ) : (
                          <h2 className="w-full text-center font-semibold py-4 text-grey-600">
                            No counseling found
                          </h2>
                        )
                      ) : (
                        <h2 className="w-full text-center font-semibold py-4 text-grey-600">
                          No counselings present for now
                        </h2>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </LayoutContext.Provider>
  );
}
